// Libraries
import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, BareModal, PrimaryButton, Button } from '@cb/apricot-react';

//Constants
import { APP_URLS } from 'src/constants';
import { ERRORS_EMPTY } from 'src/types/validation';
import { useAuth } from 'src/contexts';

// Interfaces & Typing
import { DeepPartial, Draft } from 'src/types/common';
import { IAppForm } from 'src/types/org/common/appFormSchema';
import { IDI } from 'src/types/org/di';
import { IFieldErrors } from 'src/types/validation';
import { IOrgModel } from 'src/types/org/orgInterface';
import { IRequestMeta } from 'src/types/org/request';
import { ISummary } from 'src/types';

// Classes
import { ValidationError } from 'src/types/validation';

// Components
import { Checkbox, Input } from 'src/generic/component/form';
import { DICreateFields } from './diForm';
import { FieldRow, FieldLabel, Panel, ErrorNotice, ContainerInline, HeaderInline, Loader } from 'src/generic/component';

// Processing
import { ErrorManager } from 'src/types/validation';
import { saveDIForm, submitDIForm } from 'src/di/apis';
import { IData, validate as validateData } from 'src/types/org/common/dataSchema';
import { validate as validateRequest } from 'src/types/org/di/diRequestSchema';
const formType = 'create';
interface IDIRequest {
  recordCategoryType: string;
  userId: string;
  categoryId: string;
  data: IData;
  meta: IRequestMeta;

}
interface IDIRequestProps {
  summary: ISummary;
  request: Draft<IDIRequest>;
  readOnly: boolean;
  source: string;
  searchParams: object;
}

export const DIRequestForm: FC<IDIRequestProps> = ({ summary, request: initial, readOnly, source, searchParams }) => {
  const navigate = useNavigate();
  const { userName, userEmail } = useAuth();
  const [step, setStep] = useState('di');


  const [data, setData] = useState<Draft<IData>>(initial?.data || {});
  const [org, setOrg] = useState<Draft<IOrgModel>>(initial?.data?.org || {});
  const [di, setDI] = useState<Draft<DeepPartial<IDI>>>(initial?.data?.di || {});
  const [appForm, setAppForm] = useState<Draft<IAppForm>>(initial?.data?.appForm || {});

  const metaInitial = {
    creator: userName,
    creatorEmail: userEmail,
    submitter: readOnly ? initial?.meta?.submitter : undefined,
    disclaimer: readOnly ? initial?.meta?.disclaimer : undefined,
  };
  const [meta, setMeta] = useState<Draft<IRequestMeta>>(metaInitial);
  const draft: Draft<IDIRequest> = {
    recordCategoryType: 'di',
    categoryId: summary.categoryId,
    userId: userName,
    data: {
      appForm,
      org,
      di,
    },
    meta,
  };

  useEffect(() => {
    setData(initial?.data || {});
    setOrg(initial?.data?.org || {});
    setDI(initial?.data?.di || {});
    setAppForm(initial?.data?.appForm || {});
    // If form is still able to be editted, we want to force a sign-off one every new submission
    setMeta(metaInitial);
  }, [initial]);

  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>('');
  const [errorMessageOpen, setErrorMessageOpen] = useState<boolean>(false);
  const [diErrors, setDIErrors] = useState<IFieldErrors>(ERRORS_EMPTY);
  const [orgErrors, setOrgErrors] = useState<IFieldErrors>(ERRORS_EMPTY);
  const [appFormErrors, setAppFormErrors] = useState<IFieldErrors>(ERRORS_EMPTY);
  const [metaErrors, setMetaErrors] = useState<IFieldErrors>(ERRORS_EMPTY);
  const { getError: getMetaError } = ErrorManager(metaErrors);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const response = await saveDIForm(draft);
      setErrorMessage('');
      setErrorMessageOpen(false);
      setSubmitting(false);
      return response;
    } catch (error: any) {
      let message = 'An unknown server error occurred, the save was unsuccessful.';
      if (error instanceof ValidationError) {
        const { getFieldError } = ErrorManager(error.fieldErrors);
        setDIErrors(getFieldError('data.di'));
        setOrgErrors(getFieldError('data.org'));
        setAppFormErrors(getFieldError('data.appForm'));
        setValidating(true);
        message = 'The save failed as some of the entered fields are invalid.';
      } else {
        console.log(error?.message || error);
      }
      setErrorMessageOpen(true);
      setErrorMessage(message);
      setSubmitting(false);
    }
  };

  const validateDIData = (data: Draft<IData>) => {
    try {
      const validated = validateData(data || {}, formType);
      setDIErrors(ERRORS_EMPTY);
      return validated;
    } catch (error: any) {
      console.log(error.fieldErrors);
      if (error instanceof ValidationError) {
        setDIErrors(error.fieldErrors);
        setOrgErrors(error.fieldErrors);
        setAppFormErrors(error.fieldErrors);
      } else {
        setErrorMessage(error);
      }
    }
  };

  const handleSubmit = async () => {
    setValidating(true);
    setSubmitting(true);
    try {
      if (validateRequest(draft, formType)) {
        await submitDIForm(draft);
        navigate(APP_URLS.DIHOME);
      }
    } catch (error: any) {
      if (error instanceof ValidationError) {
        const { getFieldError } = ErrorManager(error.fieldErrors);
        setDIErrors(getFieldError('data.di'));
        setAppFormErrors(getFieldError('data.appForm'));
        setOrgErrors(getFieldError('data.org'));
        setMetaErrors(getFieldError('meta'));
      } else {
        setErrorMessage(error);
      }
    }
    setSubmitting(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setErrorMessageOpen(false);
    setErrorMessage(false);
  }, [step]);

  const scrollToError = () => {
    const element = document.querySelector<HTMLElement>('.cb-validation-error');
    if (element) {
      const top = element.getBoundingClientRect().top;
      window.scrollBy({ top: top - 100 });
    }
  };

  const handleNext = async () => {
    let isValid = false;
    let nextStep = 'submit';

    if (!readOnly) {
      if (validateDIData(draft.data || {})) {
        if (await handleSave()) isValid = true;
      } else {
        setValidating(true);
        setErrorMessageOpen(true);
        setErrorMessage(<div>There were invalid fields still in the form. Look for errors near your fields.</div>);
      }
    } else {
      isValid = true;
    }

    if (isValid) setStep(nextStep);
  };

  const SaveButton: FC = () => {
    return (
      <PrimaryButton
        style={{ marginBottom: '10px', width: '100%' }}
        onClick={async () => {
          await handleSave();
        }}
        disabled={submitting}>
        Save
      </PrimaryButton>
    );
  };

  let breadcrumb = 'di-breadcrumb';
  let submitBreadcrumb = breadcrumb;
  let diBreadcrumb = breadcrumb;

  if (step === 'di') {
    diBreadcrumb += ' active';
  }
  const submitDisabled = false;

  let form = <Loader />;
  if (step === 'di') {
    form = (
      <>
        <DICreateFields
          categoryId={summary.categoryId}
          item={data}
          userId={userName}
          readOnly={readOnly}
          errors={diErrors}
          onUpdate={async (data) => {
            if (validating) validateDIData(data);
            setData(data);
            setAppForm({ ...data?.appForm });
            setOrg({ ...data?.org });
            setDI({ ...data?.di });
          }}
        />
        <br />
        {!readOnly && <SaveButton />}
        <PrimaryButton style={{ marginRight: '10px', marginBottom: '10px', width: '100%' }} onClick={handleNext} disabled={submitting}>
          Next
        </PrimaryButton>
        <Button
          style={{ width: '100%' }}
          onClick={() => source === 'SEARCH' ? navigate(APP_URLS.DISEARCH, { state: { search: searchParams } }) : navigate(APP_URLS.DIHOME)}
          disabled={submitting}>{readOnly ? 'Close' : 'Cancel'}
        </Button>
      </>
    );
  }

  if (step === 'submit') {
    form = (
      <>
        <Panel>
          <h4 style={{ marginBottom: '20px' }}>Disclaimer</h4>
          <FieldRow>
            <FieldLabel variant="body1" style={{ marginBottom: '5px' }}>
              By submitting this request I confirm that all of the information provided is true and accurately describes
              the organization named on this form and have the authority to make this request. I understand that if any
              of the information is false, deactivation of the score send code and/or legal action may result.
            </FieldLabel>
            <Checkbox
              disabled={readOnly}
              label="Accept"
              style={{ display: 'inline-block' }}
              checked={meta.disclaimer || false}
              onChange={() => {
                setMeta({ ...meta, disclaimer: meta.disclaimer ? !meta.disclaimer : true });
              }}
            />
            {getMetaError('disclaimer') && (
              <span className="cb-input-helper cb-validation-error">You must agree to the disclaimer</span>
            )}
          </FieldRow>
          <FieldRow>
            <FieldLabel variant="body1">Submitter</FieldLabel>
            <Input
              disabled={readOnly}
              validation={getMetaError('submitter') ? 'error' : undefined}
              validationMsg={getMetaError('submitter')}
              onChange={(e: any) => {
                setMeta({ ...meta, submitter: e.target.value });
              }}
              value={meta.submitter}
            // TODO: this need to clear if they can update and re-submit??
            />
          </FieldRow>
        </Panel>
        {!readOnly &&
          <PrimaryButton
            style={{ marginRight: '10px', width: '100%' }}
            onClick={async () => {
              await handleSubmit();
            }}
            disabled={submitting}>
            Submit
          </PrimaryButton>}
        <Button
          style={{ width: '100%' }}
          onClick={() => navigate(APP_URLS.DIHOME)}
          disabled={submitting}>{readOnly ? 'Close' : 'Cancel'}
        </Button>
      </>
    );
  }

  return (
    <div>
      {/* @ts-ignore  */}
      <Modal
        withHeader={false}
        withFooter={true}
        open={errorMessageOpen}
        onClose={() => {
          setErrorMessageOpen(false);
          scrollToError();
        }}>
        <ContainerInline>
          <HeaderInline style={{ marginBottom: '6px' }}>
            <i
              style={{ marginRight: '10px' }}
              className="cb-icon cb-icon-circular cb-exclamation cb-red1-color"
              aria-hidden="true"></i>
            <h4 className="cb-notification-title">
              <strong>Errors Occurred</strong>
            </h4>
          </HeaderInline>
          <div style={{ marginLeft: '42px' }} className="cb-notification-content">
            {errorMessage}
          </div>
        </ContainerInline>
      </Modal>

      {/* @ts-ignore */}
      <BareModal open={submitting} withFooter={false} withHeader={false}>
        <Loader />
      </BareModal>
      <div style={{ display: 'flex' }}>
        <div
          className={diBreadcrumb}
          style={{ marginRight: '10px' }}
          onClick={() => {
            setStep('di');
          }}>
          Institution Info
        </div>{' '}
        &gt;
        <div
          className={submitBreadcrumb}
          style={{ marginRight: '10px', marginLeft: '10px' }}
          onClick={() => {
            if (!submitDisabled) setStep('submit');
          }}>
          Submitter Info
        </div>
      </div>

      {errorMessage && (
        <div style={{ marginBottom: '20px', marginTop: '20px' }}>
          <ErrorNotice errorMessage={errorMessage} />
        </div>
      )}
      {form}
    </div>
  );
};
